import { AuthorizationsObject, OAuthProfile } from '@dat/angular-auth';
import {
    WebTracerProvider,
    SpanProcessor,
    SpanExporter,
} from '@opentelemetry/sdk-trace-web';

export const LOG_OTEL_ERROR = `[ERROR]`;
export const LOG_OTEL_LOG = `[LOG]`;
export const LOG_OTEL_DEBUG = `[DEBUG]`;
export const LOG_OTEL_WARN = `[WARN]`;

export enum LogOtelExporters {
    Console = 'Console',
    EndPoint = 'EndPoint',
    Unknown = '',
}
export enum LogOtelProcessors {
    Batch = 'Batch',
    Simple = 'Simple',
    Unknown = '',
}

export interface ILogOtelConfigProcessor {
    type: LogOtelProcessors | string;
}

export interface ILogOtelConfigExporter {
    type: LogOtelExporters;
    processor: LogOtelProcessors;
    settings?: unknown;
}

export interface ILogOtelUtilsService {
    getProvider(): WebTracerProvider;
    getTracer();
    initialize();
    setUp();
    registerIntrumentations();
    setContexts();
    addExporters(exporters: ILogOtelConfigExporter[]);
    replaceExporters(exporters: ILogOtelConfigExporter[]);
    addExporter(exporter: ILogOtelConfigExporter): SpanProcessor;
    setSpanProcessor(
        exporter: ILogOtelConfigExporter,
        spanExporter: SpanExporter,
    ): SpanProcessor;
    getExporter(exporter: ILogOtelConfigExporter): SpanExporter;
    setProvider();
}

export interface LogOtelUser {
    id: string;
    oAuthProfile?: OAuthProfile;
    authorizations?: AuthorizationsObject;
    capabilities?: Record<string, boolean>;
}

export interface ILogOtelSpanProcessorService extends SpanProcessor {
    _attributes: Record<string, string | boolean | number>;

    setDefaultAttributes(): void;
    setUser(user: LogOtelUser, capabilities: string[]): void;
    updateAttributes(
        attributes: Record<string, string | boolean | number>,
    ): void;
    createUserKey(key: string): string;
}

export interface ILogOtelConfig {
    serviceName: string;
    serviceVersion: string;
    exporters: ILogOtelConfigExporter[];
    processors?: ILogOtelConfigProcessor[];
}

export interface IDatiqLogConfig {
    otel: ILogOtelConfig;
}

export interface LogOtelConfig {
    serviceName: string;
    serviceVersion: string;
    exporters: ILogOtelConfigExporter[];
    processors?: ILogOtelConfigProcessor[];
}

export const LOG_OTEL_SERVICE_NAME = 'datiq-log-otel-service-name';

export const emptyLogOtelConfigExporter: ILogOtelConfigExporter = {
    type: LogOtelExporters.Unknown,
    processor: LogOtelProcessors.Batch,
};

export const emptyLogOtelConfig: ILogOtelConfig = {
    serviceName: LOG_OTEL_SERVICE_NAME,
    serviceVersion: '0.0.1',
    exporters: [] as ILogOtelConfigExporter[],
    processors: [] as ILogOtelConfigProcessor[],
};

export const getLogOtelBrowser = (userAgent: string): string => {
    const browser = userAgent.toLowerCase();
    const browsers = {
        chrome: /chrome/.test(browser),
        safari: /safari/.test(browser),
        firefox: /firefox/.test(browser),
        ie: /internet explorer/.test(browser),
    };

    const browserName = Object.keys(browsers).find((key) => browsers[key]);

    return browserName ?? 'unknown';
};
export const getLogOtelBrowserVersion = (userAgent: string): string => {
    const browser = userAgent.toLowerCase();
    const browserName: string = getLogOtelBrowser(userAgent);

    return browser.split(`${browserName}/`)[1]?.split(' ')?.[0] ?? 'unknown';
};

const addPrefix: (prefix: string, message: string) => string = (
    prefix: string,
    message: string,
) => `${prefix} - ${message}`;

export const LogOtelMessageError: (message: string) => string = (
    message: string,
): string => addPrefix(LOG_OTEL_ERROR, message);
export const LogOtelMessageLog: (message: string) => string = (
    message: string,
): string => addPrefix(LOG_OTEL_LOG, message);
export const LogOtelMessageDebug: (message: string) => string = (
    message: string,
): string => addPrefix(LOG_OTEL_DEBUG, message);
export const LogOtelMessageWarn: (message: string) => string = (
    message: string,
): string => addPrefix(LOG_OTEL_WARN, message);
export const LogOtelMessage: (level: string, message: string) => string = (
    level: string,
    message: string,
): string => addPrefix(level, message);
